import { useAudio } from '@/app/audio';
import { AudioControls } from '@/components/layout/audio-bar';
import { DraftScope, useDraftGuard } from '@/lib/use-draft-guard';
import { Fragment, useId } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  Archive,
  MoreHorizontal,
  CheckCheck,
  Pencil,
  Plus,
  Circle,
  CheckCircle2,
  ShieldOff,
  Star,
  Trash2,
  Copy,
  Phone,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu as Menu } from 'radix-ui';
import { useContactDrawer, type ContactDrawerHint } from '@/app/contact-drawer';
import { useWorkspace } from '@/app/workspace';
import { qk, invalidateCallData } from '@/app/query';
import { primeCallAnalyses } from '@/app/call-analyses';
import { api } from '@/lib/api';
import type { IdentityPatch } from '@/lib/api/contacts';
import type { Call } from '@/lib/types';
import { errorMessage } from '@/lib/utils';
import { formatDateTime, formatDate, formatDayLabel, formatTime, formatDuration } from '@/lib/format';
import { rangeLabel } from '@/lib/dates';
import { telLink, whatsappLink } from '@/lib/phone';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/misc';
import { useConfirm } from '@/components/ui/confirm';
import { Drawer, QueryState, Status } from '@/components/ui/system';
import { CallOutcome, PlayCall } from './desk-tools';
import { CallAi } from './call-ai';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/overlay';
import { ContactLabelEditor } from './contact-editors';
import { useTableFieldEditor } from './table-field-editor';
import { ContactTasks } from './tasks';
export function ContactDrawer() {
  const { phone, hint } = useContactDrawer();
  return phone ? (
    <ContactBody key={`${phone}:${JSON.stringify(hint?.scope)}`} phone={phone} hint={hint} />
  ) : null;
}
function ContactBody({ phone, hint }: { phone: string; hint: ContactDrawerHint | null }) {
  const nameHint = hint?.name;
  const scope = hint?.scope;
  const audio = useAudio();
  const draftScope = useId();
  const { orgId } = useWorkspace();
  const confirm = useConfirm();
  const { close } = useContactDrawer();
  const contact = useQuery({
    queryKey: qk.contact(orgId, phone),
    queryFn: () => api.contacts.getContact(orgId, phone),
  });
  const calls = useInfiniteQuery({
    queryKey: qk.contactCalls(orgId, phone, scope ?? null),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api.calls.listContactCalls(orgId, phone, pageParam, 25, scope),
    getNextPageParam: (last, all) => (all.length * 25 < last.total ? all.length + 1 : undefined),
  });
  const analysisIds = (calls.data?.pages.flatMap((page) => page.rows) ?? [])
    .filter((call) => call.recordingUrl)
    .map((call) => call.id);
  const initialAnalyses = useQuery({
    queryKey: ['contactAnalyses', orgId, phone, analysisIds],
    queryFn: () => primeCallAnalyses(analysisIds),
    enabled: analysisIds.length > 0,
  });
  const c = contact.data;
  const save = useMutation({
    mutationFn: (patch: IdentityPatch) => api.contacts.saveIdentity(orgId, phone, patch),
    onSuccess: async () => {
      await invalidateCallData(orgId);
      toast.success('Contact updated');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const review = useMutation({
    mutationFn: () => api.calls.markContactReviewed(orgId, phone),
    onSuccess: () => {
      invalidateCallData(orgId);
      toast.success('All calls reviewed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const exclude = () =>
    confirm({
      title: 'Do not track calls from this number?',
      description: 'Future calls with this number will not be tracked. Existing history is kept.',
      tone: 'primary',
      confirmLabel: 'Do not track',
      action: async () => {
        await api.exclusions.addExclusion(orgId, { phone, name: c?.name || undefined, kind: 'untracked' });
        await invalidateCallData(orgId);
      },
      successMessage: 'Number excluded',
    });
  const destroy = () =>
    confirm({
      title: 'Delete this contact’s data?',
      description: 'All call logs, recordings and notes for this number will be removed.',
      requireText: 'DELETE',
      action: async () => {
        await api.contacts.deleteContactData(orgId, phone);
        await invalidateCallData(orgId);
        close();
      },
      successMessage: 'Contact data deleted',
    });
  const guardClose = useDraftGuard(false, save.isPending || review.isPending, draftScope);
  const fieldEditor = useTableFieldEditor(() => invalidateCallData(orgId));
  const currentName = c?.name ?? nameHint ?? '';
  const editName = () =>
    fieldEditor.start(
      `contact:${phone}:name`,
      'Contact name',
      currentName,
      (name) => api.contacts.saveIdentity(orgId, phone, { name: name.trim() }),
      false,
      phone,
    );
  const editNote = () =>
    fieldEditor.start(
      `contact:${phone}:note`,
      'Contact note',
      c?.note || '',
      (note) => api.contacts.saveIdentity(orgId, phone, { note }),
      true,
      currentName || phone,
    );
  const history = calls.data?.pages.flatMap((page) => page.rows) || [];
  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success('Phone number copied');
    } catch {
      toast.error('Could not copy phone number');
    }
  };
  return (
    <>
      <Drawer
        className="contact-drawer"
        open
        backCloses={false}
        onClose={() => guardClose(() => fieldEditor.guardAction(close))}
        title={currentName || phone}
        heading={
          <div className="contact-header-identity">
            <Avatar name={currentName} seed={phone} size="sm" />
            <div className="contact-header-text">
              <button
                className="contact-header-edit"
                title={currentName || phone}
                aria-label="Edit contact name"
                disabled={!c || save.isPending}
                onClick={editName}
              >
                <span>{currentName || 'Unnamed contact'}</span>
                <Pencil size={13} />
              </button>
              <button
                type="button"
                className="contact-header-phone"
                aria-label={`Copy phone number ${phone}`}
                title="Copy phone number"
                onClick={copyPhone}
              >
                <span>{phone}</span>
                <span className="contact-header-copy" aria-hidden="true">
                  <Copy size={12} />
                </span>
              </button>
            </div>
          </div>
        }
        footer={
          audio.track ? (
            <div className="drawer-audio" role="region" aria-label="Recording player">
              <AudioControls showContactLink={false} />
            </div>
          ) : undefined
        }
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Contact actions">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem icon={<Pencil />} disabled={!c || save.isPending} onSelect={editName}>
                Edit contact name
              </DropdownMenuItem>
              <Menu.Item asChild>
                <a className="contact-menu-link" href={telLink(phone)}>
                  <Phone size={16} />
                  <span>Call contact</span>
                </a>
              </Menu.Item>
              <Menu.Item asChild>
                <a className="contact-menu-link" href={whatsappLink(phone)} target="_blank" rel="noreferrer">
                  <MessageCircle size={16} />
                  <span>WhatsApp</span>
                </a>
              </Menu.Item>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                icon={<Star />}
                disabled={!c || save.isPending}
                onSelect={() => save.mutate({ isStarred: !c?.isStarred })}
              >
                {c?.isStarred ? 'Unstar contact' : 'Star contact'}
              </DropdownMenuItem>
              {!scope && (
                <DropdownMenuItem
                  icon={<CheckCheck />}
                  disabled={!c?.unreviewedCount || review.isPending}
                  onSelect={() => review.mutate()}
                >
                  Review all calls
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                icon={<Archive />}
                disabled={!c || save.isPending}
                onSelect={() => save.mutate({ isArchived: !c?.isArchived })}
              >
                {c?.isArchived ? 'Unarchive contact' : 'Archive contact'}
              </DropdownMenuItem>
              <DropdownMenuItem icon={<ShieldOff />} disabled={!c} onSelect={exclude}>
                Do not track calls
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={<Trash2 />} disabled={!c} tone="danger" onSelect={destroy}>
                Delete contact data
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        <QueryState pending={contact.isPending} error={contact.error} onRetry={() => contact.refetch()}>
          <DraftScope id={draftScope}>
            <div className="contact-details">
              {c?.isArchived && (
                <div className="contact-archive-status">
                  <Status>Archived contact</Status>
                  <span>Hidden from default lists</span>
                </div>
              )}
              <section className="contact-section contact-context" aria-label="Contact notes and labels">
                <div className="contact-context-labels">
                  <h3>Labels</h3>
                  <ContactLabelEditor
                    labels={c?.labels || []}
                    disabled={save.isPending}
                    onSave={(labels) => save.mutateAsync({ labels })}
                  />
                </div>
                <div className="contact-context-note">
                  <h3>Contact note</h3>
                  <button
                    type="button"
                    className={`contact-note-trigger ${c?.note ? 'call-note-preview' : 'call-note-add'}`}
                    aria-label={c?.note ? 'Edit contact note' : 'Add contact note'}
                    disabled={!c || save.isPending}
                    onClick={editNote}
                  >
                    {!c?.note && <Plus size={14} aria-hidden="true" />}
                    <span>{c?.note || 'Add contact note'}</span>
                    {c?.note && <Pencil size={13} />}
                  </button>
                </div>
              </section>
              <ContactTasks phone={phone} name={c?.name ?? hint?.name ?? null} label={c?.labels[0] ?? null} />
              <section className="contact-activity" aria-label="Call activity summary">
                {scope ? (
                  <p className="text-sm text-fg-muted">
                    {calls.isPending
                      ? 'Loading'
                      : calls.error
                        ? 'Unavailable'
                        : (calls.data?.pages[0]?.total ?? 0)}{' '}
                    calls · {rangeLabel(scope.range)}
                    {scope.employeeId ? ' for the selected employee' : ''}. Newest first.
                  </p>
                ) : (
                  <>
                    <dl className="contact-stats">
                      <div>
                        <dt>Total calls</dt>
                        <dd>{c?.totalCalls ?? '—'}</dd>
                      </div>
                      <div>
                        <dt>Connected</dt>
                        <dd>{c?.totalConnected ?? '—'}</dd>
                      </div>
                      <div>
                        <dt>Talk time</dt>
                        <dd>{formatDuration(c?.totalDuration || 0)}</dd>
                      </div>
                      <div>
                        <dt>Needs review</dt>
                        <dd>{c?.unreviewedCount ?? '—'}</dd>
                      </div>
                    </dl>
                  </>
                )}
              </section>
              <section className="contact-history" aria-labelledby={`${draftScope}-history`}>
                <div className="contact-history-heading">
                  <div>
                    <h3 id={`${draftScope}-history`}>Call history</h3>
                  </div>
                  {!scope && (
                    <Button
                      variant="secondary"
                      size="xs"
                      loading={review.isPending}
                      onClick={() => review.mutate()}
                      disabled={!c?.unreviewedCount}
                    >
                      <CheckCheck />
                      Review all
                    </Button>
                  )}
                </div>
                <QueryState
                  pending={calls.isPending || (analysisIds.length > 0 && initialAnalyses.isPending)}
                  error={calls.error}
                  empty={!history.length}
                  onRetry={() => calls.refetch()}
                  emptyTitle="No call history yet"
                  emptyDescription="Calls with this person will appear here after your team’s phones sync."
                >
                  {history.map((call, index) => (
                    <Fragment key={call.id}>
                      {(index === 0 ||
                        formatDate(history[index - 1].callTime) !== formatDate(call.callTime)) && (
                        <h4 className="contact-history-date">{formatDayLabel(call.callTime)}</h4>
                      )}
                      <HistoryCard
                        call={call}
                        onEditNote={() =>
                          fieldEditor.start(
                            `call:${call.id}:note`,
                            'Call note',
                            call.note || '',
                            (note) => api.calls.updateCall(orgId, call.id, { note }),
                            true,
                            `${currentName || phone} · ${formatDateTime(call.callTime)}`,
                          )
                        }
                      />
                    </Fragment>
                  ))}
                </QueryState>
                {calls.hasNextPage && (
                  <Button
                    variant="secondary"
                    className="contact-load-more"
                    loading={calls.isFetchingNextPage}
                    onClick={() => calls.fetchNextPage()}
                  >
                    Load more calls
                  </Button>
                )}
              </section>
            </div>
          </DraftScope>
        </QueryState>
      </Drawer>
      {fieldEditor.modal}
    </>
  );
}
function HistoryCard({ call, onEditNote }: { call: Call; onEditNote: () => void }) {
  const { orgId } = useWorkspace();
  const update = useMutation({
    mutationFn: (reviewed: boolean) => api.calls.updateCall(orgId, call.id, { reviewed }),
    onSuccess: async () => {
      await invalidateCallData(orgId);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <article className="call-card" aria-label={`${call.type} call at ${formatDateTime(call.callTime)}`}>
      <div className="call-card-head">
        <CallOutcome call={call} />
        <div className="flex items-center gap-1">
          <PlayCall call={call} />
          <Button
            variant="ghost"
            size="xs"
            className="history-review-action"
            aria-pressed={call.reviewed}
            aria-label={call.reviewed ? 'Mark call not reviewed' : 'Mark call reviewed'}
            disabled={update.isPending}
            onClick={() => update.mutate(!call.reviewed)}
            disabledReason="Please wait while the current action finishes."
          >
            {call.reviewed ? (
              <CheckCircle2 className="text-primary" />
            ) : (
              <Circle className="text-fg-subtle" />
            )}
            <span>{call.reviewed ? 'Reviewed' : 'Review'}</span>
          </Button>
        </div>
      </div>
      <p className="call-card-meta">
        <time title={formatDateTime(call.callTime)}>{formatTime(call.callTime)}</time>
        <span>{call.employeeName || 'Unknown employee'}</span>
      </p>
      <button
        className={call.note ? 'call-note-preview' : 'call-note-add'}
        aria-label={call.note ? 'Edit call note' : 'Add call note'}
        onClick={onEditNote}
      >
        {!call.note && <Plus size={14} aria-hidden="true" />}
        <span>{call.note || 'Add call note'}</span>
        {call.note && <Pencil size={13} />}
      </button>
      {call.recordingUrl && <CallAi call={call} />}
    </article>
  );
}
